import React, {useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {classes, style} from './text-box.st.css';
import {RenderFunction, useInstanceValue, useObservableValue} from '../index';
import {
    assertDefined,
    assertNotNullable,
    Evaluable,
    evaluateFunction,
    evaluateWhenFunction, HtmlTextDirection,
    ObservableValue, OmitStrict, stringReplaceEmptyValue, uuidCreate,
    Values
} from '@wix/devzai-utils-common';
import {elementSelectTextRange, KeyCodes} from '@wix/devzai-utils-dom';
import { reactCreateMemo } from "../react-utils/react-utils";

export const TextBoxTypes = {
    Text: 'text',
    Number: 'number',
    Email: 'email',
    PhoneNumber: 'tel',
    Password: 'password',
    Url: 'url'
} as const;

export type TextBoxTypes = Values<typeof TextBoxTypes>;

export const TextBoxInputMode = {
    PhoneNumber: 'tel',
    Email: 'email',
    Url: 'url',
    Numeric: 'numeric',
    Decimal: 'decimal',
    Text: 'text',
    Search: 'search',
} as const;

export type TextBoxInputMode = Values<typeof TextBoxInputMode>;

export const TextBoxAutoComplete = {
    Off: 'off',
    On: 'on',
    Url: 'url',
    PersonFullName: 'name',
    FirstName: 'given-name',
    LastName: 'family-name',
    Nickname: 'nickname',
    Username: 'username',
    Email: 'email',
    StreetAddress: 'street-address',
    CountryCode: 'country',
    CountryName: 'country-name',
    PostalCode: 'postal-code',
    PhoneNumber: 'tel'
} as const;

export type TextBoxAutoComplete = Values<typeof TextBoxAutoComplete>;

export namespace TextBox {

    export type Type = TextBoxTypes;

    export interface Props extends OmitStrict<React.HTMLAttributes<any>, 'autoCapitalize'> {
        suffixContent?: Evaluable<RenderFunction>;
        prefixContent?: Evaluable<RenderFunction>;
        onTextChange?: (value: string) => void;
        onIsValidChange?: (value: boolean) => void;
        placeholder?: React.InputHTMLAttributes<HTMLInputElement>['placeholder'];
        readOnly?: React.InputHTMLAttributes<HTMLInputElement>['readOnly'];
        value?: string | number;
        defaultValue?: string | number;
        inputId?: string;
        type?: Type;
        shouldValidateValue?: boolean;
        min?: number;
        max?: number;
        step?: number | 'any';
        autoSelectTextOnFocus?: boolean | ((value: string) => boolean | [number, number]);
        autoFocus?: boolean;
        disabled?: boolean;
        autoCapitalize?: boolean;
        onEnter?: (textBox: TextBox) => void;
        name?: React.InputHTMLAttributes<HTMLInputElement>['name'];
        autoComplete?: React.InputHTMLAttributes<HTMLInputElement>['autoComplete'];
        inputMode?: TextBoxInputMode;
        excludeFromForm?: boolean;
        dir?: HtmlTextDirection;
    }
}

export interface TextBox {
    getInputElement () : HTMLInputElement;
    getRootElement () : HTMLElement;

    getText () : string;

    isValid () : boolean;

    getObservableText () : ObservableValue<string>;

    getObservableIsValid () : ObservableValue<boolean>;

    getValidationMessage () : string | undefined;

    focus () : void;
}

export const TextBox = reactCreateMemo(React.forwardRef(function TextBox (props: TextBox.Props, ref: React.Ref<TextBox>) {

    const {
        suffixContent,
        prefixContent,
        onTextChange,
        onIsValidChange,
        value,
        defaultValue,
        placeholder,
        name,
        autoComplete,
        inputMode,
        readOnly,
        inputId,
        type = TextBoxTypes.Text,
        shouldValidateValue = true,
        min,
        max,
        step = 'any',
        autoSelectTextOnFocus = false,
        autoFocus = false,
        disabled,
        autoCapitalize = false,
        excludeFromForm = false,
        onEnter,
        dir,

        className,
        style: styleAttributes,
        ...htmlAttributes
    } = props;

    const normalizedAutoComplete = stringReplaceEmptyValue(autoComplete, undefined) ?? evaluateFunction(() => {
        switch (type) {
            case TextBoxTypes.Url: return TextBoxAutoComplete.Url;
            case TextBoxTypes.PhoneNumber: return TextBoxAutoComplete.PhoneNumber;
            case TextBoxTypes.Email: return TextBoxAutoComplete.Email;
            default: return undefined;
        }
    })

    const normalizedInputName = (
        !name &&
        normalizedAutoComplete &&
        normalizedAutoComplete !== TextBoxAutoComplete.Off &&
        normalizedAutoComplete !== TextBoxAutoComplete.On
    ) ? normalizedAutoComplete : name;

    const [inFocus, setInFocus] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);

    const observableText = useInstanceValue(() => new ObservableValue<string>((value ?? (defaultValue ?? '')).toString()));
    const observableIsValid = useInstanceValue(() => new ObservableValue<boolean>(true));

    const isValid = assertDefined(useObservableValue(observableIsValid));

    const normalizedInputMode = inputMode ?? evaluateFunction<React.HTMLAttributes<any>['inputMode']>(() => {
        switch (type) {
            case TextBoxTypes.PhoneNumber:
                return 'tel';
            case TextBoxTypes.Email:
                return 'email';
            case TextBoxTypes.Url:
                return 'url';
            case TextBoxTypes.Number:
                return 'numeric';
            default:
                return undefined;
        }
    })

    const randomFormId = useMemo(() => uuidCreate(), []);

    const textBox = useMemo(() => {
        return {
            getInputElement() {
                return assertNotNullable(inputRef.current);
            },

            getRootElement () {
                return assertNotNullable(rootRef.current)
            },

            getText () {
                return assertNotNullable(inputRef.current).value;
            },

            isValid () {
                return observableIsValid.getValue();
            },

            getObservableText () {
                return observableText;
            },

            getObservableIsValid() {
                return observableIsValid;
            },

            getValidationMessage () {
                return inputRef.current?.validationMessage;
            },

            focus () {
                inputRef.current?.focus()
            }
        }
    }, []);

    useImperativeHandle(ref, () => textBox, [textBox]);

    useEffect(() => {
        const input = inputRef.current;

        if (input && inFocus) {
            const autoSelect = evaluateWhenFunction(autoSelectTextOnFocus, input.value);
            if (autoSelect) {
                if (autoSelect === true) {
                    // We'll select the text on timeout to avoid floating helper to appear on mobile.
                    // NOTE: In IOS the floating helper still appears, and requires a longer timeout (~400ms).
                    // Maybe we should test the user agent, and increase the timeout for IOS.
                    setTimeout(() => {
                        input.select()
                    }, 0)
                } else {
                    const [start, end] = autoSelect;
                    elementSelectTextRange(input, start, end);
                }
            }
        }
    }, [inFocus]);

    const normalizedDir = evaluateFunction(() => {
        switch (normalizedInputMode) {
            case TextBoxInputMode.PhoneNumber:
            case TextBoxInputMode.Email:
            case TextBoxInputMode.Numeric:
            case TextBoxInputMode.Url:
                return HtmlTextDirection.Ltr;
            default:
                return dir;
        }
    })

    return (
        <div
            {...htmlAttributes}
            ref={rootRef}
            className={style(classes.root, {focus: inFocus, isValid: isValid}, props.className)}
            style={{
                ...styleAttributes
            }}
        >
            {evaluateWhenFunction(prefixContent)}
            <input
                id={inputId}
                type={type}
                value={value}
                defaultValue={defaultValue}
                placeholder={placeholder}
                name={normalizedInputName}
                autoComplete={normalizedAutoComplete}
                readOnly={readOnly}
                disabled={disabled}
                className={style(classes.input, {})}
                ref={inputRef}
                inputMode={normalizedInputMode}
                dir={normalizedDir}
                form={excludeFromForm ? randomFormId : undefined}
                step={type === TextBoxTypes.Number ? step : undefined}
                min={type === TextBoxTypes.Number ? min : undefined}
                max={type === TextBoxTypes.Number ? max : undefined}
                autoCapitalize={autoCapitalize ? undefined : 'off'}
                autoFocus={autoFocus ? true : undefined}
                onChange={event => {

                    const inputElement = event.target as HTMLInputElement;

                    if (shouldValidateValue) {
                        const prevIsValid = observableIsValid.getValue();
                        const updatedIsValid = inputElement.checkValidity();

                        if (prevIsValid !== updatedIsValid) {
                            observableIsValid.setValue(updatedIsValid);

                            if (onIsValidChange) {
                                onIsValidChange(updatedIsValid)
                            }
                        }
                    }

                    const updatedValue = inputElement.value;

                    observableText.setValue(updatedValue);

                    if (onTextChange)
                    {
                        onTextChange(updatedValue)
                    }
                }}
                onFocus={() => {
                    setInFocus(true);
                }}
                onBlur={() => {
                    setInFocus(false);
                }}
                onKeyDown={event => {
                    if (event.key === KeyCodes.Enter) {
                        onEnter?.(textBox);
                    }
                }}
            />
            {evaluateWhenFunction(suffixContent)}
        </div>
    );
}));

export function textBoxShouldUseAutoTextDirection (
    options: {
        type: TextBoxTypes | undefined;
        inputMode: TextBoxInputMode | undefined;
    }
) {
    const {
        type,
        inputMode
    } = options;

    const normalizedInputMode = inputMode ?? evaluateFunction<TextBoxInputMode>(() => {
        switch (type) {
            case TextBoxTypes.PhoneNumber:
                return 'tel';
            case TextBoxTypes.Email:
                return 'email';
            case TextBoxTypes.Url:
                return 'url';
            case TextBoxTypes.Number:
                return 'numeric';
            default:
                return 'text';
        }
    })

    switch (normalizedInputMode) {
        case TextBoxInputMode.Text:
        case TextBoxInputMode.Search: {
            return true;
        }
        case TextBoxInputMode.Numeric:
        case TextBoxInputMode.Decimal:
        case TextBoxInputMode.PhoneNumber:
        case TextBoxInputMode.Email:
        case TextBoxInputMode.Url: {
            return false;
        }
    }
}